import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AlertService } from 'app/services/alert.service';
import { LoggerService } from 'app/services/logger.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { VenueService } from '../services/venue.service';

import * as VenueActions from './venue.actions';

@Injectable()
export class VenueEffects {
    private _actions$ = inject(Actions);
    private _store = inject(Store);
    private logger = inject(LoggerService);
    private _venueService = inject(VenueService);
    private _alertService = inject(AlertService);

    messages = {
        getAllError: 'Error getting all venues',
        conectionError: 'Error connecting to the server',
        unknownError: 'Unknown error',
    };

    // -----------------------------------------------------------
    // @ Effects
    // -----------------------------------------------------------

    // Get All Venues
    getAllVenues$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VenueActions.loadVenues),
            switchMap(() => {
                return this._venueService.getAll().pipe(
                    map((res) =>
                        VenueActions.loadVenuesSuccess({ venues: res.data })
                    ),
                    catchError((error) =>
                        of(
                            VenueActions.loadVenuesFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                );
            })
        )
    );

    // Get One Venue
    getVenue$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VenueActions.loadVenue),
            switchMap((action) =>
                this._venueService.getOne(action.id).pipe(
                    map((res) =>
                        VenueActions.loadVenueSuccess({
                            venue: res.data,
                        })
                    ),
                    catchError((error) =>
                        of(
                            VenueActions.loadVenueFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Create Venue
    createVenue$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VenueActions.createVenue),
            switchMap((action) =>
                this._venueService.create(action.data).pipe(
                    map((res) =>
                        VenueActions.createVenueSuccess({
                            venue: res.data,
                            success: 'Venue created successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            VenueActions.createVenueFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Update Venue
    updateVenue$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VenueActions.updateVenue),
            switchMap((action) =>
                this._venueService.update(action.data, action.id).pipe(
                    map((res) =>
                        VenueActions.updateVenueSuccess({
                            venue: res.data,
                            success: 'Venue updated successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            VenueActions.updateVenueFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // Delete Venue
    deleteVenue$ = createEffect(() =>
        this._actions$.pipe(
            ofType(VenueActions.deleteVenue),
            switchMap((action) =>
                this._venueService.delete(action.id).pipe(
                    map(() =>
                        VenueActions.deleteVenueSuccess({
                            id: action.id,
                            success: 'Venue deleted successfully',
                        })
                    ),
                    catchError((error) =>
                        of(
                            VenueActions.deleteVenueFailure({
                                error:
                                    error?.error?.message ||
                                    this.messages.unknownError,
                            })
                        )
                    )
                )
            )
        )
    );

    // -----------------------------------------------------------
    // @ Alerts
    // -----------------------------------------------------------

    onVenueActionSuccess$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    VenueActions.createVenueSuccess,
                    VenueActions.updateVenueSuccess,
                    VenueActions.deleteVenueSuccess
                ),
                tap((action) => {
                    this._alertService.showAlert(action.success, 'success');
                })
            ),
        { dispatch: false }
    );

    onVenueActionFailure$ = createEffect(
        () =>
            this._actions$.pipe(
                ofType(
                    VenueActions.loadVenuesFailure,
                    VenueActions.loadVenueFailure,
                    VenueActions.createVenueFailure,
                    VenueActions.updateVenueFailure,
                    VenueActions.deleteVenueFailure
                ),
                tap((action) => {
                    this._alertService.showAlert(action.error, 'error');
                })
            ),
        { dispatch: false }
    );
}
